import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { AdyenDropIn } from 'src/components/AdyenPayment/AdyenDropIn';
import { CancelButton } from 'src/components/Buttons';
import { RetailInvoicePDF } from 'src/components/RetailInvoice';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import {
  useCatalogDataContextDataSelector,
  useCatalogDataContextEventsTransformedSelector,
} from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useRetailSaleListingTickets } from 'src/hooks/api/useRetailSaleListingTickets';
import { useEventItemLoadingDisplay } from 'src/hooks/useEventItemLoadingDisplay';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { EyeIcon } from 'src/svgs/EyeIcon';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { EmptyListingQuery } from 'src/utils/eventQueryUtils';
import {
  ActionOutboxEntityType,
  EventWithData,
  ListingDetails,
  ListingQuery,
} from 'src/WebApiController';

import { CancellableFormHeader } from '../common/CancellableFormHeader';
import { HeaderContent } from '../common/EventEntityHeader/EventEntityHeader.styled';
import { Summary } from '../common/Summary';
import {
  ModalBody,
  ModalBodyDataContainer,
  ModalBodyHeaderContainer,
  ModalFooter,
} from '../Modal/Modal.styled';
import { buildOrderDetailRows } from './buildOrderDetailRows';
import { MultiListingRetailSaleCheckoutStep } from './components/MultiListingRetailSaleCheckoutStep';
import { RetailSaleBuyerStep } from './components/RetailSaleBuyerStep';
import { RetailSaleCheckoutStep } from './components/RetailSaleCheckoutStep';
import { RetailSaleInvoicePreviewStep } from './components/RetailSaleInvoicePreviewStep';
import * as styles from './RetailSale.css';
import {
  RetailSaleLocalContextProvider,
  useRetailSaleContext,
} from './RetailSaleLocalContext';
import { useRetailSaleStep } from './RetailSaleLocalContext.atoms';
import { getRetailExternalSaleId } from './RetailSaleLocalContext.utils';
import { RetailSaleFormValues } from './types';

/**
 * Single-listing entry point. Creates its own FormProvider seeded from the
 * listing entity and wraps RetailSaleWithProvider.
 *
 * For multi-listing mode (POS-8649) the caller (RetailSaleActions) wraps
 * the modal in its own FormProvider with ticketIds pre-populated and renders
 * RetailSaleWithProvider directly — this avoids the double-FormProvider that
 * shadowed ticketIds in #5707.
 */
export const RetailSale = () => {
  const { posEntity, posEntityDisplayId, posEntityId } =
    useActivePosEntityContext<ListingDetails>();

  const listing = useMemo(() => {
    return posEntity?.entityType === ActionOutboxEntityType.Listing
      ? (posEntity as ListingDetails)
      : undefined;
  }, [posEntity]);

  const { isLoading: entityIsLoading, loadingState } =
    useEventItemLoadingDisplay<ListingDetails>(
      FormatContentId.LoadingListingId,
      FormatContentId.SearchingForListingId,
      FormatContentId.CouldNotFindListingId,
      posEntityDisplayId ?? posEntityId?.toString()
    );

  const methods = useForm<RetailSaleFormValues>({
    defaultValues: {},
  });

  const eventsTransformed = useCatalogDataContextEventsTransformedSelector();
  const event = useMemo(() => {
    return eventsTransformed?.find(
      (e) => e.event?.virtId === listing?.viagVirtId
    );
  }, [eventsTransformed, listing?.viagVirtId]);

  useEffect(() => {
    if (listing) {
      const totalProceeds = listing.listPrice * listing.availQty;
      methods.reset({
        externalSaleId: getRetailExternalSaleId(listing.id),
        listingId: listing.id ?? 0,
        currencyCode: listing.currency ?? 'USD',
        saleDate: new Date().toISOString(),
        useShippingAddressOnFile: false,
        billingAddressSameAsShipping: true,
        tags: listing.tags,
        quantitySold: listing.availQty,
        section: listing.seating.section,
        row: listing.seating.row,
        seatFrom: listing.seating.seatFr,
        seatTo: listing.seating.seatTo,
        marketplacePaymentLines: [],
        ticketType: listing.topPriTktType ?? undefined,
        inHandDate: listing.inHandAt ?? undefined,
        totalNetProceeds: totalProceeds,
        // External payment defaults
        paymentMethodChoice: 'adyen',
        externalPaymentMethodType: null,
        externalPaymentNote: null,
        externalPaymentAmount: totalProceeds > 0 ? totalProceeds : null,
        externalPaymentDate: new Date().toISOString().split('T')[0],
      });
    }
  }, [listing, methods]);

  if (!listing && entityIsLoading) {
    return loadingState;
  }

  return (
    <FormProvider {...methods}>
      <RetailSaleWithProvider event={event} listing={listing} />
    </FormProvider>
  );
};

/**
 * Renders the RetailSale modal content inside RetailSaleLocalContextProvider.
 * Expects a FormProvider to already be mounted by the caller — this is the
 * shared entry used by both the single-listing flow (RetailSale, above) and
 * the multi-listing flow (RetailSaleActions → MultiListingRetailSaleModal).
 *
 * POS-8649 — Bug fix #1: avoid double-FormProvider. The original prototype
 * mounted a second FormProvider inside RetailSale that shadowed ticketIds
 * seeded by the bulk-action caller. Splitting RetailSale (provider) from
 * RetailSaleWithProvider (no provider) lets multi-listing callers share a
 * single, top-level form state.
 */
export const RetailSaleWithProvider = ({
  event,
  listing,
}: {
  event?: EventWithData;
  listing?: ListingDetails;
}) => {
  return (
    <RetailSaleLocalContextProvider event={event} listing={listing}>
      <RetailSaleContent event={event} listing={listing} />
    </RetailSaleLocalContextProvider>
  );
};

const RetailSaleContent = ({
  event,
  listing,
}: {
  event?: EventWithData;
  listing?: ListingDetails;
}) => {
  const { closeModal } = useContext(ModalContext);
  const onCancel = useCallback(() => closeModal(true), [closeModal]);

  const { resetStep } = useRetailSaleStep();

  useEffect(() => {
    // Reset the step when retail creation unmounted
    return () => {
      resetStep();
    };
  }, [resetStep]);

  return (
    <>
      <CancellableFormHeader onClose={onCancel}>
        <HeaderContent>
          <Stack direction="row" gap="m" alignItems="center">
            <span className={styles.breadcrumbHeader}>
              <Content id={ContentId.RetailSale} />
            </span>
            <span className={styles.breadcrumbSeparator}>&gt;</span>
            <span className={styles.breadcrumbItem}>
              <Content id={ContentId.Checkout} />
            </span>
          </Stack>
        </HeaderContent>
      </CancellableFormHeader>

      <ModalBody>
        {event?.event && (
          <ModalBodyHeaderContainer>
            <Summary
              event={event.event}
              posEntity={listing}
              entityType={ActionOutboxEntityType.Listing}
            />
          </ModalBodyHeaderContainer>
        )}

        <ModalBodyDataContainer>
          <ModalBodyContent listing={listing} event={event} />
        </ModalBodyDataContainer>
      </ModalBody>

      <ModalFooter>
        <ModalFooterContent
          onCancel={onCancel}
          event={event}
          listing={listing}
        />
      </ModalFooter>
    </>
  );
};

const ModalBodyContent = ({
  listing,
  event,
}: {
  listing?: ListingDetails;
  event?: EventWithData;
}) => {
  const { step } = useRetailSaleStep();
  const {
    adyenSession,
    onAdyenPaymentCompleted,
    onAdyenError,
    isMultiListing,
  } = useRetailSaleContext();
  if (adyenSession) {
    return (
      <AdyenDropIn
        sessionId={adyenSession.sessionId}
        sessionData={adyenSession.sessionData}
        clientKey={adyenSession.clientKey}
        environment={adyenSession.environment}
        countryCode={adyenSession.countryCode}
        onPaymentCompleted={onAdyenPaymentCompleted}
        onError={onAdyenError}
      />
    );
  }

  switch (step) {
    case 'detail':
      return <RetailSaleBuyerStep listing={listing} />;
    case 'checkout':
      // Multi-listing mode (POS-8649) renders a ticket-centric checkout step
      return isMultiListing ? (
        <MultiListingRetailSaleCheckoutStep />
      ) : (
        <RetailSaleCheckoutStep />
      );
    case 'invoicePreview':
      return <RetailSaleInvoicePreviewStep listing={listing} event={event} />;
    default:
      return null;
  }
};

const ModalFooterContent = ({
  onCancel,
  event,
  listing,
}: {
  onCancel: () => void;
  event?: EventWithData;
  listing?: ListingDetails;
}) => {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { step, goToCheckout, goToInvoicePreview } = useRetailSaleStep();
  const {
    disabled,
    adyenSession,
    selectedBuyer,
    onUpsertBuyer,
    onCompleteAsUnpaid,
    onCompleteExternalPayment,
    onPayAndComplete,
    isMultiListing,
  } = useRetailSaleContext();
  const { loginContext } = useAppContext();

  const previewInvoiceMsg = useContent(ContentId.PreviewInvoice);
  const backToCheckoutMsg = useContent(ContentId.BackToCheckout);
  const ticketProceedsMsg = useContent(ContentId.TicketProceeds);

  const { venue } = useGetEventFullInfo(event?.event ?? null);
  const { showErrorDialog } = useClientLogContext();
  const { watch } = useFormContext<RetailSaleFormValues>();
  const input = watch();

  // POS-8630: resolve event/venue PER LISTING for the multi-event invoice.
  // Hooks are called unconditionally at the top level (Rules of Hooks); the
  // listing query no-ops (returns []) when not in multi-listing mode.
  const eventsTransformed = useCatalogDataContextEventsTransformedSelector();
  const catalogData = useCatalogDataContextDataSelector();
  const listingFilterQuery = useMemo((): ListingQuery | null => {
    const ids = input.allListingIds ?? [];
    if (!input.perListingSelections?.length || ids.length === 0) return null;
    return { ...EmptyListingQuery, entityIds: ids };
  }, [input.allListingIds, input.perListingSelections?.length]);
  const { data: listingTickets } =
    useRetailSaleListingTickets(listingFilterQuery);

  const isValidBuyer = useMemo(() => {
    // If we have a fully-resolved buyer with addresses, nothing more to check.
    if (
      selectedBuyer != null &&
      selectedBuyer.shippingAddressLine1 != null &&
      selectedBuyer.billingAddressLine1 != null
    ) {
      return true;
    }

    // No fully-resolved buyer — require the same manual fields as
    // the single-listing flow: name, email, phone, shipping address, billing.
    return (
      !!input.buyerName &&
      !!input.buyerEmail &&
      !!input.buyerPhone &&
      !!input.shippingStreet &&
      !!input.shippingState &&
      !!input.shippingCountry &&
      !!input.shippingZipCode &&
      (input.billingAddressSameAsShipping ||
        (!!input.billingStreet &&
          !!input.billingState &&
          !!input.billingCountry &&
          !!input.billingZipCode))
    );
  }, [
    input.billingAddressSameAsShipping,
    input.billingCountry,
    input.billingState,
    input.billingStreet,
    input.billingZipCode,
    input.buyerEmail,
    input.buyerName,
    input.buyerPhone,
    input.shippingCountry,
    input.shippingState,
    input.shippingStreet,
    input.shippingZipCode,
    selectedBuyer,
  ]);

  const canContinueToPayment = useMemo(() => {
    const baseValid =
      isValidBuyer &&
      !!input.saleDate &&
      input.totalNetProceeds != null &&
      input.totalNetProceeds > 0 &&
      !!input.externalSaleId &&
      !!input.ticketType;

    if (isMultiListing) {
      // Multi-listing (POS-8649): ticketIds may still be loading (empty array)
      // — don't block on quantitySold here; the submit path validates it.
      return baseValid;
    }
    // Single-listing mode: require quantitySold
    return baseValid && input.quantitySold != null && input.quantitySold > 0;
  }, [
    input.externalSaleId,
    input.quantitySold,
    input.saleDate,
    input.ticketType,
    input.totalNetProceeds,
    isMultiListing,
    isValidBuyer,
  ]);

  const onMoveToNext = useCallback(async () => {
    const ok = await onUpsertBuyer();
    if (ok) goToCheckout();
  }, [goToCheckout, onUpsertBuyer]);

  const handleDownloadInvoice = useCallback(async () => {
    setIsDownloading(true);
    try {
      const values = input;
      const proceeds = values.totalNetProceeds ?? 0;
      const marketplaceLinesCost =
        values.marketplacePaymentLines?.reduce(
          (sum, item) => sum + (item.amount ?? 0),
          0
        ) ?? 0;
      const totalAmount = proceeds + marketplaceLinesCost;

      const blob = await pdf(
        <RetailInvoicePDF
          brokerName={loginContext?.user?.activeAccount?.accountName ?? ''}
          buyerName={values.buyerName}
          buyerEmail={values.buyerEmail}
          buyerAddress={[
            values.shippingStreet,
            values.shippingCity,
            values.shippingState,
            values.shippingCountry,
          ]
            .filter(Boolean)
            .join(', ')}
          invoiceNumber={values.externalSaleId ?? `${values.listingId}`}
          invoiceDate={values.saleDate ?? new Date().toISOString()}
          currency={values.currencyCode ?? 'USD'}
          paymentTermsDays={values.paymentTermsDays}
          lineItems={[
            ...(proceeds !== 0
              ? [
                  {
                    description: ticketProceedsMsg,
                    amount: proceeds,
                    costType: 'Proceeds',
                  },
                ]
              : []),
            ...(values.marketplacePaymentLines ?? []).map((line) => ({
              description: line.description ?? '',
              ratePercent: line.ratePercent ?? undefined,
              amount: line.amount ?? 0,
              costType: (line.amount ?? 0) < 0 ? 'Credit' : 'Charge',
            })),
          ]}
          status={values.paymentReceived === true ? 'Paid' : 'Outstanding'}
          totalAmount={totalAmount}
          balanceDue={totalAmount}
          paymentsMade={0}
          brokerNotes={values.privateNotes}
          orderDetailRows={buildOrderDetailRows({
            values,
            event,
            venue,
            totalAmount,
            listing,
            listingTickets,
            eventsTransformed,
            catalogData,
          })}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RetailInvoice_${values.listingId}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      showErrorDialog('RetailSale.handleDownloadInvoice', error as Error, {
        trackErrorData: { listingId: input.listingId },
      });
    } finally {
      setIsDownloading(false);
    }
  }, [
    catalogData,
    event,
    eventsTransformed,
    input,
    listing,
    listingTickets,
    loginContext,
    showErrorDialog,
    ticketProceedsMsg,
    venue,
  ]);

  // When adyen drop in session present, we are actioning based on adyen callbacks
  if (adyenSession) {
    return null;
  }

  if (step === 'detail') {
    return (
      <Stack direction="row" gap="m" justifyContent="end" width="full">
        <CancelButton type="button" onClick={onCancel} />
        <Button
          type="button"
          onClick={onMoveToNext}
          disabled={!canContinueToPayment}
        >
          <Content id={ContentId.ContinueToPayment} />
        </Button>
      </Stack>
    );
  }

  if (step === 'checkout') {
    const isExternalPayment = input.paymentMethodChoice === 'external';
    const isUnpaid = input.paymentMethodChoice === 'unpaid';

    // Validate external payment form fields for submit-button enabled state
    const canSubmitExternal =
      isExternalPayment &&
      input.externalPaymentMethodType != null &&
      (input.externalPaymentAmount ?? 0) > 0 &&
      !!input.externalPaymentDate &&
      (input.externalPaymentNote == null ||
        input.externalPaymentNote.length <= 500);

    return (
      <Stack direction="row" justifyContent="spaceBetween" width="full">
        <Stack direction="row">
          <CancelButton
            textContentId={ContentId.Back}
            type="button"
            disabled={disabled}
            onClick={onCancel}
          />
        </Stack>
        <Stack direction="row" gap="m">
          <CancelButton type="button" disabled={disabled} onClick={onCancel} />
          <Button type="button" variant="outline" onClick={goToInvoicePreview}>
            <Stack direction="row" gap="s" alignItems="center">
              <EyeIcon size={vars.iconSize.s} />
              {previewInvoiceMsg}
            </Stack>
          </Button>
          {isExternalPayment ? (
            <Button
              type="button"
              onClick={onCompleteExternalPayment}
              disabled={disabled || !canSubmitExternal}
            >
              <Content id={ContentId.CompleteExternalPayment} />
            </Button>
          ) : isUnpaid ? (
            <Button
              type="button"
              onClick={onCompleteAsUnpaid}
              disabled={disabled}
            >
              <Content id={ContentId.CompleteAsUnpaid} />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={onPayAndComplete}
              disabled={disabled}
            >
              <Content id={ContentId.PayAndComplete} />
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }

  if (step === 'invoicePreview') {
    return (
      <Stack direction="row" gap="m" justifyContent="spaceBetween" width="full">
        <Button type="button" variant="outline" onClick={goToCheckout}>
          {backToCheckoutMsg}
        </Button>
        <Button
          type="button"
          onClick={handleDownloadInvoice}
          disabled={isDownloading}
        >
          <Content id={ContentId.Download} />
        </Button>
      </Stack>
    );
  }

  return null;
};
